import React, { useMemo, useState } from 'react'
import { cn } from '@utils/safeTs'
import styles from './FlussoPersonalizzazionePosizioneLoghi.module.sass'
import useTrans from '@hooks/useTrans'
import { Alert, Button, ButtonIcon, FormField, FormFile, MyAccountImagePreview } from '@components/atoms'
import { Bin, Info, Replace, Upload } from '@components/icons'
import { PosizionePersonalizzazioneType } from '@utils/types'
import { EDITOR_FILE_EXTENSIONS } from '@utils/editor-bozze'
import { FieldArray } from 'formik'


interface Props {
  posizione: PosizionePersonalizzazioneType
  values: any
  setFieldValue: (name?: string, value?: any) => void
  variant?: 'caricamento' | 'approvazione' | 'loghi'
  isEditable?: boolean
  edited: boolean
  setEdited: (v: boolean) => void
  loghiEdited: boolean
  setLoghiEdited: (v: boolean) => void
  className?: string
}

const FlussoPersonalizzazionePosizioneLoghi = (props: Props) => {

  const {
    posizione,
    values,
    setFieldValue,
    variant = 'approvazione', 
    isEditable = false,
    edited,
    setEdited,
    loghiEdited = false,
    setLoghiEdited,
    className = '',
  } = props

  const t = useTrans()
  const allowedExtensions = useMemo(
    () => (posizione.tecnica?.estensioniAmmesse || EDITOR_FILE_EXTENSIONS)
      .map((extension) => extension?.trim()?.toLowerCase())
      .filter(Boolean),
    [posizione.tecnica?.estensioniAmmesse]
  )
  const logoAccept = allowedExtensions.length > 0 ? allowedExtensions.map((extension) => `.${extension}`).join(',') : undefined
  const formatiConsentitiLabel = allowedExtensions.length > 0 ? allowedExtensions.map((extension) => `.${extension}`).join(', ') : ""

  return (
    <div className={cn(styles.root, styles[`root--${variant}`], className)}>
      <p className={styles.title}>{t('Loghi')}</p>
      <FieldArray
        name="loghi"
        render={arrayHelpers => (
          <>
            <div className={styles.body}>
              {values.loghi?.length ? (
                <div className={styles.list}>
                  {values.loghi?.map((l, index) => (
                    <MyAccountImagePreview 
                      key={index}
                      url={l.logo?.url}
                      name={l.logo?.name}
                      showUnsupportedFilename
                      className={styles.logoPreview}
                      onRemove={isEditable ? () => {
                        arrayHelpers.remove(index)
                        if (variant == 'caricamento' && Object.keys(values).includes('bozzaDigitaleImage') && !values.fileBozzaDigitaleImage) {
                          setFieldValue('bozzaDigitaleImage', null)
                        }
                        setLoghiEdited(true)
                        setEdited(true)
                      } : undefined}
                    />
                  ))}
                </div>
              ) : (
                <div className={styles.empty}>
                  {t("Nessun logo impostato")}
                </div>
              )}
              {isEditable ? (
                <>
                  {variant !== 'loghi' && (
                    <Alert 
                      full
                      styleVariant='basic'
                      variant='muted'
                      icon={<Info />}
                      placeholder={t(variant === 'approvazione' 
                        ? "Modificare i loghi richiederà la creazione di una nuova bozza digitale (come rifiutare la bozza attuale)"
                        : "Modificare i loghi richiederà il caricamento di una nuova bozza digitale")}
                      size="xs"
                    />
                  )}
                  <div className={styles.bottom}>
                    <div className={styles.actions}>
                      <div className={styles.uploadBox}>
                        <FormField className={styles.uploadButton}>
                          <FormFile
                            id={`${posizione.id}_logo`}
                            name="fileLogo"
                            onChange={(event) => {
                              arrayHelpers.push({
                                logo: event.target.files?.[0] ? {
                                  url: URL.createObjectURL(event.target.files?.[0]),
                                  name: event.target.files?.[0]?.name
                                } : null,
                                fileLogo: event.target.files?.[0],
                              })
                              if (variant == 'caricamento' && Object.keys(values).includes('bozzaDigitaleImage') && !values.fileBozzaDigitaleImage) {
                                setFieldValue('bozzaDigitaleImage', null)
                              }
                              setLoghiEdited(true)
                              setEdited(true)
                            }}
                            variant={'button'}
                            accept={logoAccept}
                            buttonProps={{ 
                              label: t(variant === 'caricamento' ? 'Aggiungi' : 'Aggiungi logo'), 
                              variant: 'secondary', 
                              size: 'small', 
                              icon: <Upload />, 
                              iconPos: 'left' 
                            }}
                          />
                        </FormField>
                      </div>
                      <Button 
                        label={t(variant === 'caricamento' ? "Ripristina" : "Ripristina loghi iniziali")} 
                        onClick={() => {
                          setFieldValue('loghi', posizione.loghi?.map(l => ({
                            logo: l,
                            fileLogo: undefined,
                          })) || [])
                          if (variant == 'caricamento' 
                            && Object.keys(values).includes('bozzaDigitaleImage') 
                            && !values.fileBozzaDigitaleImage
                            && posizione.statoPosizionePersonalizzazione !== 'BOZZA_RIFIUTATA' // se è in bozza rifiutata, di default è vuota
                          ){
                            setFieldValue('bozzaDigitaleImage', posizione.bozzaDigitaleImage)
                          }
                          setLoghiEdited(false)
                        }}
                        variant="secondary"
                        size="small"
                        icon={<Replace />}
                        icon_pos='left'
                        disabled={!loghiEdited}
                      />
                    </div>
                    <p className={styles.helperText}>
                      {t('Formati consentiti: {0}', [formatiConsentitiLabel])}
                    </p>
                  </div>
                </>
              ) : <></>}
            </div>
          </>
        )}
      />
    </div>
  )
}

export default FlussoPersonalizzazionePosizioneLoghi
